import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage, tokens } from '@/lib/api';
import {
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Toast,
} from '@/components/ui';
import { GleeLogo, GleeMark } from '@/components/Logo';
import {
  FALLBACK,
  UI_VERSION,
  applyAccent,
  type Branding as BrandingValues,
} from '@/lib/branding';

/**
 * Branding & Theme.
 *
 * What the platform calls itself, what it looks like, and who is credited at
 * the bottom of every page.
 *
 * The preview is live and it is the point of the screen. A colour picker whose
 * effect you discover by saving and navigating away is a colour picker nobody
 * uses twice — so the accent is applied to the real stylesheet as it changes,
 * and reverted if the page is left without saving.
 */

const PRESETS: Array<{ name: string; accent: string; accent_to: string }> = [
  { name: 'Glee', accent: '#2563eb', accent_to: '#7c3aed' },
  { name: 'Ocean', accent: '#0284c7', accent_to: '#0d9488' },
  { name: 'Sunset', accent: '#e11d48', accent_to: '#f59e0b' },
  { name: 'Forest', accent: '#059669', accent_to: '#65a30d' },
  { name: 'Graphite', accent: '#475569', accent_to: '#1e293b' },
];

export default function Branding() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BrandingValues | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);

  const current = useQuery({
    queryKey: ['branding'],
    queryFn: () => api.get<BrandingValues>('/branding'),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (current.data) setDraft({ ...FALLBACK, ...current.data });
  }, [current.data]);

  // The live half of the preview. Reverted on unmount, so leaving without
  // saving does not carry an unsaved colour into every other page.
  useEffect(() => {
    if (draft) applyAccent(draft.accent, draft.accent_to);
  }, [draft?.accent, draft?.accent_to]);

  useEffect(
    () => () => {
      const saved = queryClient.getQueryData<BrandingValues>(['branding']);
      applyAccent(
        saved?.accent ?? FALLBACK.accent,
        saved?.accent_to ?? FALLBACK.accent_to,
      );
    },
    [queryClient],
  );

  const save = useMutation({
    mutationFn: () =>
      api.put<BrandingValues>('/admin/branding', {
        product_name: draft?.product_name,
        tagline: draft?.tagline,
        accent: draft?.accent,
        accent_to: draft?.accent_to,
        company_name: draft?.company_name,
        company_url: draft?.company_url,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(['branding'], result);
      setToast({ message: 'Branding saved.', tone: 'good' });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not save'), tone: 'bad' }),
  });

  const clearLogo = useMutation({
    mutationFn: () => api.delete('/admin/branding/logo'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setToast({ message: 'Back to the built-in logo.', tone: 'good' });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not clear'), tone: 'bad' }),
  });

  /**
   * Uploaded with fetch rather than the JSON client: this is multipart, and
   * the client sets a JSON content type the server would reject.
   */
  async function uploadLogo(file: File) {
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch('/api/v1/admin/branding/logo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokens.access() ?? ''}` },
        body,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error?.message ?? 'Upload failed');
      }
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setToast({ message: 'Logo uploaded.', tone: 'good' });
    } catch (err) {
      setToast({ message: errorMessage(err, 'Upload failed'), tone: 'bad' });
    } finally {
      setUploading(false);
    }
  }

  if (!draft) return null;

  const set = (patch: Partial<BrandingValues>) =>
    setDraft((d) => (d ? { ...d, ...patch } : d));

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Branding & Theme"
        description="What this platform calls itself, what it looks like, and who is credited at the bottom of every page."
        actions={
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save branding
          </Button>
        }
      />

      {/* ---------------------------------------------------------------- */}
      <Card title="Preview" description="Exactly what the sidebar and the sign-in page will show.">
        <div className="grid gap-4 sm:grid-cols-[15rem_1fr]">
          {/* The sidebar, at its real width and its real colour. A swatch
              would not show the thing that actually goes wrong, which is a
              gradient mark disappearing into a dark panel. */}
          <div className="overflow-hidden rounded-lg bg-ink-950 p-3">
            <div className="flex items-center gap-2.5">
              {draft.logo_url ? (
                <img
                  src={draft.logo_url}
                  alt=""
                  className="h-7 w-7 shrink-0 rounded-md object-contain"
                />
              ) : (
                <GleeMark size={28} mono className="shrink-0 text-white/90" />
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">
                  {draft.product_name || 'Product name'}
                </p>
                <p className="truncate text-[11px] uppercase tracking-wide text-white/60">
                  Super Admin
                </p>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <div
                className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
                style={{ background: 'var(--brand-gradient)' }}
              >
                Overview
              </div>
              <div className="rounded-md px-3 py-1.5 text-sm text-white/70">Clients</div>
              <div className="rounded-md px-3 py-1.5 text-sm text-white/70">Costs</div>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center rounded-lg border border-line bg-sunken p-6">
            {draft.logo_url ? (
              <>
                <img
                  src={draft.logo_url}
                  alt=""
                  className="h-12 w-auto max-w-[18rem] object-contain"
                />
                {draft.tagline && (
                  <p className="mt-2 text-center text-sm text-ink-500">{draft.tagline}</p>
                )}
              </>
            ) : (
              <GleeLogo
                height={44}
                name={draft.product_name || 'Glee AI'}
                tagline={draft.tagline}
              />
            )}
            <div className="mt-5 flex gap-2">
              <button
                className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
                style={{ background: 'var(--brand-gradient)' }}
              >
                Primary action
              </button>
              <button className="rounded-md border border-line px-3 py-1.5 text-sm">
                Secondary
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card title="Name and logo" className="mt-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Product name" hint="Shown in the sidebar, the sign-in page and the footer.">
            <Input
              value={draft.product_name}
              onChange={(e) => set({ product_name: e.target.value })}
              placeholder="Glee AI"
            />
          </Field>
          <Field label="Tagline" hint="Under the logo on the sign-in page. Leave empty for none.">
            <Input
              value={draft.tagline ?? ''}
              onChange={(e) => set({ tagline: e.target.value })}
              placeholder="Voice AI Agents for a Smarter Tomorrow"
            />
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
          {draft.logo_url ? (
            <img
              src={draft.logo_url}
              alt=""
              className="h-10 w-auto max-w-[12rem] rounded object-contain"
            />
          ) : (
            <GleeMark size={40} />
          )}
          <div className="flex-1 min-w-[14rem]">
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="text-sm"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadLogo(file);
                e.target.value = '';
              }}
            />
            <p className="mt-1 text-xs text-ink-500">
              PNG, JPEG, SVG or WebP, up to 2MB. Stored in the database, so it
              is inside the same backup as everything else. A wide lockup works
              best — it is shown at 28px square in the sidebar and full width on
              the sign-in page.
            </p>
          </div>
          {draft.logo_url && (
            <Button
              variant="ghost"
              loading={clearLogo.isPending}
              onClick={() => clearLogo.mutate()}
            >
              Use built-in logo
            </Button>
          )}
        </div>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card
        title="Theme"
        description="Two colours, because the mark runs one into the other. A single flat accent beside it reads as another product's chrome."
        className="mt-4"
      >
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => {
            const active =
              preset.accent === draft.accent && preset.accent_to === draft.accent_to;
            return (
              <button
                key={preset.name}
                type="button"
                onClick={() => set({ accent: preset.accent, accent_to: preset.accent_to })}
                className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${
                  active
                    ? 'border-brand-500 ring-2 ring-brand-500/30'
                    : 'border-line hover:border-ink-300'
                }`}
              >
                <span
                  className="h-4 w-8 rounded"
                  style={{
                    background: `linear-gradient(135deg, ${preset.accent}, ${preset.accent_to})`,
                  }}
                />
                {preset.name}
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Accent (start)">
            <div className="flex gap-2">
              <input
                type="color"
                value={draft.accent}
                onChange={(e) => set({ accent: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border border-line bg-transparent"
              />
              <Input
                value={draft.accent}
                onChange={(e) => set({ accent: e.target.value })}
              />
            </div>
          </Field>
          <Field label="Accent (end)">
            <div className="flex gap-2">
              <input
                type="color"
                value={draft.accent_to}
                onChange={(e) => set({ accent_to: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border border-line bg-transparent"
              />
              <Input
                value={draft.accent_to}
                onChange={(e) => set({ accent_to: e.target.value })}
              />
            </div>
          </Field>
        </div>
        <p className="mt-2 text-xs text-ink-500">
          Set both to the same value for a flat accent. Light and dark are
          handled for you — these are the brand colours, not the page
          background, and each user chooses light or dark for themselves in the
          sidebar.
        </p>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card
        title="Footer credit"
        description="On the bottom of every page, and on the sign-in screen."
        className="mt-4"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Company">
            <Input
              value={draft.company_name ?? ''}
              onChange={(e) => set({ company_name: e.target.value })}
              placeholder="Glee Telecom Global LLC"
            />
          </Field>
          <Field label="Website" hint="Must start with https://">
            <Input
              value={draft.company_url ?? ''}
              onChange={(e) => set({ company_url: e.target.value })}
              placeholder="https://gleetelecom.com"
            />
          </Field>
        </div>

        <div className="mt-4 rounded-md border border-line bg-sunken px-4 py-3 text-xs text-ink-500">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {draft.product_name || 'Glee AI'}
              {draft.company_name && ` — powered by ${draft.company_name}`}
            </span>
            <span className="tabular-nums">v{UI_VERSION}</span>
          </div>
        </div>
        <p className="mt-2 text-xs text-ink-500">
          The version is not editable — it is the release this page was built
          from, which is the first question of every bug report.
        </p>
      </Card>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
